import React, { useState, useEffect } from 'react';
import { X, Check, ChevronRight, ChevronLeft, ImagePlus, Globe2 } from 'lucide-react';
import { AmenitiesPicker } from './AmenitiesGrid';
import { API } from '../finance/shared';

const WOMEN_FRIENDLY_OPTIONS = [
  { value: '', label: 'غير محدد' },
  { value: 'true', label: 'مناسب للسيدات' },
  { value: 'false', label: 'غير مناسب للسيدات' },
];
const ALL_HOURS = Array.from({ length: 18 }, (_, i) => 6 + i);

const STEPS = [
  { id: 1, title: 'المعلومات الأساسية', subtitle: 'اسم الملعب، نوع الرياضة، السعة والسعر' },
  { id: 2, title: 'التواصل والصورة', subtitle: 'واتساب، الموقع، الصورة والوصف' },
  { id: 3, title: 'الخدمات والتوفر', subtitle: 'الخدمات المتوفرة، ساعات العمل والنشر' },
];

const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 transition-all focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20';
const labelCls = 'mb-1.5 block text-xs font-bold text-slate-700';

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className={labelCls}>{label}{required && <span className="text-orange-500"> *</span>}</span>
    {children}
  </label>
);

const HoursPicker = ({ selected, onChange }) => {
  const toggle = (h) => onChange(selected.includes(h) ? selected.filter((x) => x !== h) : [...selected, h].sort((a, b) => a - b));
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[11px] text-slate-400">اترك القائمة فارغة للسماح بكل الساعات (6ص - 11م)</p>
        {selected.length > 0 && <button type="button" onClick={() => onChange([])} data-testid="venue-form-hours-clear" className="text-xs font-bold text-teal-600 hover:underline">كل الساعات</button>}
      </div>
      <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-9" data-testid="venue-form-hours-picker">
        {ALL_HOURS.map((h) => {
          const active = selected.includes(h);
          return (
            <button
              type="button" key={h} onClick={() => toggle(h)} data-testid={`venue-form-hour-${h}`}
              className={`rounded-lg border px-1.5 py-1.5 text-xs font-bold transition-all active:scale-95 ${active ? 'border-teal-500 bg-teal-500/10 text-teal-700 shadow-sm ring-1 ring-teal-500/30' : 'border-slate-200 text-slate-500 hover:scale-[1.02] hover:border-teal-400'}`}
            >
              {String(h).padStart(2, '0')}:00
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default function VenueWizardModal({ open, editing, form, setForm, imageFile, setImageFile, saving, onSave, onClose }) {
  const [step, setStep] = useState(1);
  useEffect(() => { if (open) setStep(1); }, [open]);
  if (!open) return null;

  const set = (patch) => setForm({ ...form, ...patch });
  const canGoNext = step !== 1 || form.name.trim().length > 0;
  const next = () => canGoNext && setStep((s) => Math.min(3, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));
  const imagePreview = imageFile ? URL.createObjectURL(imageFile) : (editing?.image_storage_path ? `${API}/venues/${editing.id}/image` : null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="venue-modal-wizard" dir="rtl">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-2xl">
        <div className="shrink-0 bg-[#0A192F] px-6 py-5 text-white">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-extrabold sm:text-xl">{editing ? 'تعديل الملعب' : 'ملعب جديد'}</h3>
              <p className="mt-0.5 text-xs text-slate-400">{STEPS[step - 1].title} — {STEPS[step - 1].subtitle}</p>
            </div>
            <button onClick={onClose} data-testid="venue-wizard-close-btn" className="shrink-0 rounded-full p-1.5 text-slate-400 transition-colors hover:bg-white/10 hover:text-white"><X className="h-5 w-5" /></button>
          </div>
          <div className="flex items-center gap-2">
            {STEPS.map((s, idx) => (
              <React.Fragment key={s.id}>
                <span
                  data-testid={`venue-wizard-step-indicator-${s.id}`}
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all ${step >= s.id ? 'bg-gradient-to-br from-teal-400 to-emerald-400 text-[#0A192F] shadow-[0_0_12px_rgba(45,212,191,0.4)]' : 'bg-slate-800 text-slate-500'}`}
                >
                  {step > s.id ? <Check className="h-3.5 w-3.5" /> : s.id}
                </span>
                {idx < STEPS.length - 1 && (
                  <div className="h-1 flex-1 overflow-hidden rounded-full bg-slate-800">
                    <div className="h-full bg-gradient-to-r from-teal-400 to-emerald-400 transition-all duration-300 ease-out" style={{ width: step > s.id ? '100%' : '0%' }} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="min-h-[420px] flex-1 space-y-5 overflow-y-auto p-6" data-testid={`venue-wizard-step-${step}`}>
          {step === 1 && (
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
              <Field label="اسم الملعب" required><input className={inputCls} value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="venue-form-name" /></Field>
              <Field label="نوع الرياضة"><input className={inputCls} value={form.sport_type} onChange={(e) => set({ sport_type: e.target.value })} placeholder="كرة قدم، بادل..." data-testid="venue-form-sport" /></Field>
              <Field label="السعة (عدد اللاعبين)"><input type="number" min="0" className={inputCls} value={form.capacity} onChange={(e) => set({ capacity: e.target.value })} data-testid="venue-form-capacity" /></Field>
              <Field label="السعر لكل ساعة (ر.س)"><input type="number" min="0" className={inputCls} value={form.price_per_hour} onChange={(e) => set({ price_per_hour: e.target.value })} data-testid="venue-form-price" /></Field>
              <Field label="المدينة"><input className={inputCls} value={form.city} onChange={(e) => set({ city: e.target.value })} placeholder="الرياض، جدة..." data-testid="venue-form-city" /></Field>
              <Field label="المقاس"><input className={inputCls} value={form.dimensions} onChange={(e) => set({ dimensions: e.target.value })} placeholder="مثال: 11×11" data-testid="venue-form-dimensions" /></Field>
              <div className="sm:col-span-2">
                <Field label="مدى الملاءمة للسيدات">
                  <select className={inputCls} value={form.women_friendly} onChange={(e) => set({ women_friendly: e.target.value })} data-testid="venue-form-women-friendly">
                    {WOMEN_FRIENDLY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
              </div>
              {!form.name.trim() && <p className="sm:col-span-2 text-[11px] font-semibold text-orange-500">أدخل اسم الملعب للانتقال للخطوة التالية</p>}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <Field label="رقم واتساب الملعب (اختياري)"><input className={inputCls} value={form.whatsapp_number} onChange={(e) => set({ whatsapp_number: e.target.value })} placeholder="يُستخدم رقم المنشأة إن لم يُحدَّد" data-testid="venue-form-whatsapp" /></Field>
              <Field label="رابط الموقع على خرائط جوجل (اختياري)">
                <div className="relative">
                  <Globe2 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input className={`${inputCls} pr-9`} value={form.maps_url} onChange={(e) => set({ maps_url: e.target.value })} placeholder="https://maps.google.com/..." data-testid="venue-form-maps-url" />
                </div>
              </Field>
              <Field label="صورة الملعب الرئيسية">
                <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-4 text-center transition-colors hover:border-teal-400 hover:bg-teal-50/40">
                  {imagePreview ? (
                    <img src={imagePreview} alt="معاينة" className="h-32 w-full rounded-lg object-cover" />
                  ) : (
                    <>
                      <ImagePlus className="h-6 w-6 text-slate-400" />
                      <span className="text-xs font-semibold text-slate-500">اضغط لرفع صورة الملعب (JPG, PNG, WebP)</span>
                    </>
                  )}
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setImageFile(e.target.files?.[0] || null)} className="hidden" data-testid="venue-form-image" />
                </label>
              </Field>
              <Field label="وصف الملعب"><textarea className={inputCls} rows={3} value={form.description} onChange={(e) => set({ description: e.target.value })} data-testid="venue-form-description" /></Field>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <Field label="الخدمات المتوفرة"><AmenitiesPicker selected={form.amenities} onChange={(amenities) => set({ amenities })} testId="venue-form-amenity" /></Field>
              <Field label="ساعات العمل المتاحة للحجز"><HoursPicker selected={form.operating_hours} onChange={(operating_hours) => set({ operating_hours })} /></Field>
              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="venue-form-is-public-card">
                <div>
                  <p className="text-sm font-bold text-slate-800">نشر هذا الملعب في السوق العام</p>
                  <p className="mt-0.5 text-[11px] text-slate-500">يظهر للزوار في قسم "ملاعب منشآت تسيار" على /venues</p>
                </div>
                <button
                  type="button" onClick={() => set({ is_public: !form.is_public })} data-testid="venue-form-is-public"
                  className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${form.is_public ? 'bg-teal-500' : 'bg-slate-300'}`}
                >
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-all ${form.is_public ? 'right-0.5' : 'right-5'}`} />
                </button>
              </label>
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between border-t border-slate-100 bg-slate-50 px-6 py-4">
          <button onClick={step === 1 ? onClose : prev} data-testid="venue-wizard-prev-btn" className="flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 transition-transform active:scale-95 hover:bg-slate-100">
            {step === 1 ? 'إلغاء' : (<><ChevronRight className="h-4 w-4" /> السابق</>)}
          </button>
          {step < 3 ? (
            <button
              onClick={next} disabled={!canGoNext} data-testid="venue-wizard-next-btn"
              className="flex items-center gap-1 rounded-lg bg-[#16233b] px-5 py-2 text-sm font-bold text-white transition-transform active:scale-95 hover:bg-[#1e2f4e] disabled:cursor-not-allowed disabled:opacity-40"
            >
              التالي <ChevronLeft className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={onSave} disabled={saving} data-testid="venue-form-submit"
              className="flex items-center gap-2 rounded-lg bg-gradient-to-l from-teal-500 to-emerald-500 px-5 py-2 text-sm font-bold text-white shadow-md transition-transform active:scale-95 hover:shadow-lg disabled:opacity-50"
            >
              {saving ? 'جاري الحفظ...' : 'حفظ الملعب'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
